'use client';
import { Pause, PlayArrow, Restore } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useEffect, useRef } from 'react';
import { useCourses } from '@/providers';
import { useViewStore } from '@/stores';
import { toast } from '@/utils/client/toast';

dayjs.extend(duration);

/*
  Initializes from processingTime
  Runs every second if in running state
  Persists time via updateTimerRemaining
  Tracks when drawer closes and subtracts elapsed time only if the timer is running
  Pausing or resetting stores the remaining time
  Keeps time paused if paused before drawer closes
*/
export default function TimerPanel() {
  const { selectedCourse, courseStatus, updateTimerRemaining, isLoading } = useCourses();

  const {
    showMenuDrawer,
    timer: { time, isRunning, closedAt, setTime, setRunning, setClosedAt },
  } = useViewStore();

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Ticking logic
  useEffect(() => {
    if (isRunning && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setTime((prev) => Math.max(prev - 1, 0));
      }, 1000);
    }

    if (!isRunning && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning, setTime]);

  // React to drawer open/close
  useEffect(() => {
    if (!selectedCourse) return;

    if (!showMenuDrawer) {
      if (isRunning) {
        setClosedAt(new Date());
        updateTimerRemaining(time);
      }
      return;
    }

    const initialTime = courseStatus?.timerRemaining ?? selectedCourse.processingTime ?? 0;

    if (time === 0 && initialTime > 0) {
      setTime(initialTime);
    }

    if (isRunning && closedAt) {
      const diff = dayjs().diff(dayjs(closedAt), 'second');
      setTime((prev) => Math.max(prev - diff, 0));
    }

    if (closedAt !== undefined) {
      setClosedAt(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showMenuDrawer,
    updateTimerRemaining,
    setClosedAt,
    time,
    selectedCourse?.processingTime,
    closedAt,
    selectedCourse,
    setTime,
    isRunning,
    courseStatus?.timerRemaining,
  ]);

  const toggleTimer = () => {
    if (!selectedCourse) return;

    if (isRunning) {
      setRunning(false);
      updateTimerRemaining(time);
      toast.info('Timer pausiert');
    } else {
      setRunning(true);
      toast.info('Timer gestartet');
    }
  };

  const resetTimer = () => {
    if (!selectedCourse) return;
    const resetValue = selectedCourse.processingTime ?? 0;
    setTime(resetValue);
    updateTimerRemaining(resetValue);
    setRunning(false);
    toast.info('Timer zurückgesetzt');
  };

  const formatted = dayjs.duration(time * 1000).format('HH:mm:ss');

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
      {isLoading ? (
        <CircularProgress size={20} />
      ) : (
        <>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 18 }}>{formatted}</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title={isRunning ? 'Pause' : 'Start'}>
              <IconButton onClick={toggleTimer} size="small">
                {isRunning ? <Pause /> : <PlayArrow />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Zurücksetzen">
              <IconButton onClick={resetTimer} size="small">
                <Restore />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );
}
